import { FC, useCallback, useEffect, useState } from "react";
import { Button, Stack, Tooltip } from "@mui/material";
import { useDidMount } from "rooks";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { dashboardDimensionsCategoryIdsByDashboardIdSelector } from "@/store/dashboards/selectors/dashboard/filters/dashboardDimensionsCategoryIdsByDashboardIdSelector";
import { getDimensionsThunk } from "@/store/dimensions/thunks/dimensions/getDimensionsThunk";
import { DashboardDimensionIdSelect } from "@/components/pages/dashboard/components/filters/dimension-category-filters/components/DashboardDimensionIdSelect";
import { DashboardCategoryIdSelect } from "@/components/pages/dashboard/components/filters/dimension-category-filters/components/DashboardCategoryIdSelect";
import { DashboardDimensionFilterRemoveButton } from "@/components/pages/dashboard/components/filters/dimension-category-filters/components/DashboardDimensionFilterRemoveButton";
import { DimensionIcon } from "@/components/pages/dimensions/components/icons/DimensionIcon";
import { currentDashboardProtectedSelector } from "@/store/dashboards/selectors/dashboard-hierarchy-visibility/currentDashboardProtectedSelector";
import { dimensionsDataSelector } from "@/store/dimensions/selectors/dimensions-data/dimensionsDataSelector";

interface DashboardDimensionAndCategoryFiltersProps {
  dashboardId: string;
}

export const DashboardDimensionAndCategoryFilters: FC<
  DashboardDimensionAndCategoryFiltersProps
> = ({ dashboardId }) => {
  const dispatch = useAppDispatch();

  const dimensions = useAppSelector(dimensionsDataSelector);
  const isProtected = useAppSelector(currentDashboardProtectedSelector);
  const [selectedDimensionId, setSelectedDimensionId] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [filterApplied, setFilterApplied] = useState(false);

  const dimensionCategoryIds = useAppSelector((state) =>
    dashboardDimensionsCategoryIdsByDashboardIdSelector(state, dashboardId),
  );

  const filterAddHandler = useCallback(() => setFilterApplied(true), []);

  const dimensionChangeHandler = useCallback((id: string) => {
    setSelectedDimensionId((oldId) => {
      if (oldId !== id) {
        setSelectedCategoryId("");
      }

      return id;
    });
  }, []);

  const dimensionRemoveHandler = useCallback(() => {
    setFilterApplied(false);
    setSelectedDimensionId("");
    setSelectedCategoryId("");
  }, []);

  useDidMount(() => {
    dispatch(getDimensionsThunk());
  });

  useEffect(() => {
    if (
      !dimensionCategoryIds?.category_id &&
      !dimensionCategoryIds?.dimension_id
    ) {
      setFilterApplied(false);
      return;
    }

    setFilterApplied(true);
    setSelectedDimensionId(dimensionCategoryIds.dimension_id ?? "");
    setSelectedCategoryId(dimensionCategoryIds.category_id ?? "");
  }, [dimensionCategoryIds]);

  return (
    <Tooltip
      arrow
      placement="top"
      title={isProtected ? "Protected dashboards cannot be modified." : ""}
    >
      {filterApplied ? (
        <Stack
          bgcolor="grey.100"
          direction="row"
          p={1}
          spacing={1}
          borderRadius={2}
          alignItems="center"
          justifyContent="center"
        >
          <DimensionIcon sx={{ fontSize: 16 }} color="primary" />

          <DashboardDimensionIdSelect
            dimensionId={selectedDimensionId}
            onDimensionIdChange={dimensionChangeHandler}
          />

          <DashboardCategoryIdSelect
            dashboardId={dashboardId}
            categoryId={selectedCategoryId}
            selectedDimensionId={selectedDimensionId}
            onCategoryIdChange={setSelectedCategoryId}
          />

          <DashboardDimensionFilterRemoveButton
            dashboardId={dashboardId}
            onRemove={dimensionRemoveHandler}
          />
        </Stack>
      ) : (
        <span>
          <Button
            size="small"
            variant="text"
            startIcon={<DimensionIcon fontSize="small" />}
            disabled={isProtected || !dimensions?.length}
            onClick={filterAddHandler}
          >
            Add Dimension Filter
          </Button>
        </span>
      )}
    </Tooltip>
  );
};
